import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtGHS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertTriangle, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const topSupplier = (lines) => {
  const c = {};
  lines.forEach((l) => { if (l.supplier_id) c[l.supplier_id] = (c[l.supplier_id] || 0) + 1; });
  return Object.keys(c).sort((a, b) => c[b] - c[a])[0] || "";
};

export const RestockPanel = ({ order, can }) => {
  const [data, setData] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/wholesale/orders/${order.id}/shortfalls`).then((r) => { setData(r.data); setSupplierId((s) => s || topSupplier(r.data.lines)); }).catch(() => {});
  useEffect(() => {
    load();
    if (can("procurement.manage")) api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, [order.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || (!data.lines.length && !data.restock_pos.length)) return null;
  const create = async () => {
    if (!supplierId) return toast.error("Select the supplier");
    setBusy(true);
    try {
      const { data: po } = await api.post(`/wholesale/orders/${order.id}/restock-po`, { supplier_id: supplierId });
      toast.success(`Draft ${po.po_number} created for ${po.supplier_name} (${fmtGHS(po.total)}) — review it under Purchase orders`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const est = data.lines.reduce((a, l) => a + l.shortfall * l.unit_cost, 0);

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 p-3 space-y-2" data-testid="ws-restock-panel">
      {data.lines.length > 0 && <>
        <div className="text-sm font-medium flex items-center gap-1.5 text-amber-800 dark:text-amber-300"><AlertTriangle className="h-4 w-4" /> {data.lines.length} line(s) short at {data.warehouse}</div>
        <ul className="text-xs space-y-0.5">
          {data.lines.map((l) => <li key={l.product_id} data-testid={`ws-short-${l.sku}`}>{l.generic_name}: ordered {l.ordered}, in stock {l.available} → <b>reorder {l.shortfall}</b></li>)}
        </ul>
        {can("procurement.manage") && (
          <div className="flex flex-wrap items-end gap-2">
            <div><Label className="text-xs">Supplier</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger className="w-56 h-9" data-testid="ws-restock-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button size="sm" disabled={busy} onClick={create} data-testid="ws-restock-po-btn"><ClipboardList className="h-4 w-4 mr-1.5" /> {busy ? "Creating…" : `Create draft PO (~${fmtGHS(est)})`}</Button>
          </div>
        )}
      </>}
      {data.restock_pos.length > 0 && (
        <div className="text-xs text-muted-foreground" data-testid="ws-restock-pos">Restock POs: {data.restock_pos.map((p) => `${p.po_number} (${p.supplier_name}, ${fmtGHS(p.total)})`).join(", ")}</div>
      )}
    </div>
  );
};
